/**
 * Regeln des Newsletter-Banners (Spec 2026-10-12 §6), als reine Funktionen,
 * damit Zeitpunkt, Ruhe und Ausschlüsse ohne DOM prüfbar sind.
 */

export const BANNER_SPEICHER_SCHLUESSEL = 'locari.newsletterBanner';
export const BANNER_VERZOEGERUNG_MS = 20_000;
export const BANNER_RUHE_TAGE = 30;
export const BANNER_MARKER = 'data-kein-newsletter-banner';

const TAG_MS = 86_400_000;

export interface BannerZustand {
  geschlossenAm?: string;
  angemeldet?: true;
}

/**
 * Website-Bereiche ohne Banner: Rechtstexte, Landeseiten (`/lp/*`, damit
 * auch die Messe-Seiten), die Messe-Seite `/messe` und die Newsletter-Seiten
 * selbst, je Sprache. Auth und Onboarding liegen außerhalb des
 * Marketing-Layouts.
 */
const AUSGESCHLOSSEN = ['legal', 'lp', 'messe', 'newsletter'] as const;

export function pfadAusgeschlossen(pfad: string): boolean {
  const segmente = pfad.split('/').filter(Boolean);
  const erstes =
    segmente[0] === 'en' || segmente[0] === 'fr' ? segmente[1] : segmente[0];

  return (AUSGESCHLOSSEN as readonly string[]).includes(erstes ?? '');
}

/**
 * Liest den gemerkten Zustand. Wirft der Speicher (privates Fenster,
 * gesperrte Website-Daten) oder steht Unlesbares darin, gilt „nichts
 * gemerkt“: das Banner darf erscheinen.
 */
export function leseBannerZustand(
  speicher: Pick<Storage, 'getItem'> | null | undefined,
): BannerZustand {
  try {
    const roh = speicher?.getItem(BANNER_SPEICHER_SCHLUESSEL);
    if (!roh) return {};

    const wert: unknown = JSON.parse(roh);
    if (!wert || typeof wert !== 'object') return {};

    const zustand: BannerZustand = {};
    if ('angemeldet' in wert && wert.angemeldet === true) {
      zustand.angemeldet = true;
    }
    if ('geschlossenAm' in wert && typeof wert.geschlossenAm === 'string') {
      zustand.geschlossenAm = wert.geschlossenAm;
    }
    return zustand;
  } catch {
    return {};
  }
}

/** Schreibt den Zustand; ohne Speicher merkt sich das Banner eben nichts. */
export function schreibeBannerZustand(
  speicher: Pick<Storage, 'setItem'> | null | undefined,
  zustand: BannerZustand,
): boolean {
  try {
    speicher?.setItem(BANNER_SPEICHER_SCHLUESSEL, JSON.stringify(zustand));
    return Boolean(speicher);
  } catch {
    return false;
  }
}

/** Nach Anmeldung nie wieder, nach Schließen 30 Tage Ruhe. */
export function zustandErlaubtBanner(zustand: BannerZustand, jetzt: Date) {
  if (zustand.angemeldet) return false;
  if (!zustand.geschlossenAm) return true;

  const geschlossen = Date.parse(zustand.geschlossenAm);
  if (Number.isNaN(geschlossen)) return true;

  return jetzt.getTime() - geschlossen >= BANNER_RUHE_TAGE * TAG_MS;
}

/**
 * Halbe Scrolltiefe: die Hälfte des scrollbaren Wegs ist zurückgelegt. Eine
 * Seite ohne Scrollweg erreicht sie nie, dort gilt nur die Zeit.
 */
export function halbeScrolltiefeErreicht(masse: {
  scrollY: number;
  fensterHoehe: number;
  dokumentHoehe: number;
}): boolean {
  const weg = masse.dokumentHoehe - masse.fensterHoehe;
  if (weg <= 0) return false;

  return masse.scrollY >= weg / 2;
}

/**
 * Darf der Zeitauslöser (20 s) das Banner jetzt zeigen? Nur, wenn die Besucherin
 * die erste Bildschirmhöhe verlassen hat, also nicht mehr auf den Seitenkopf
 * (Hero) schaut. Eine Seite ohne Scrollweg hat keinen anderen Auslöser, dort
 * gilt die Zeit allein. Entscheid des Auftraggebers nach dem Website-Audit vom
 * 30.10.2026: das Fenster nicht beim ersten Aufruf über dem Hero öffnen.
 */
export function zeitAusloeserErlaubt(masse: {
  scrollY: number;
  fensterHoehe: number;
  dokumentHoehe: number;
}): boolean {
  if (masse.dokumentHoehe - masse.fensterHoehe <= 0) return true;

  return masse.scrollY >= masse.fensterHoehe;
}

/**
 * Steht gerade etwas anderes im Vordergrund? Das Cookie-Banner von CookieYes
 * (`.cky-consent-container` ohne `cky-hide`), dessen Einstellungsfenster
 * (`.cky-modal.cky-modal-open`) oder das Buchungsfenster. Solange eines davon
 * offen ist, wartet das Newsletter-Banner.
 */
export function anderesFensterOffen(dokument: Pick<Document, 'querySelector'>) {
  return Boolean(
    dokument.querySelector('.cky-consent-container:not(.cky-hide)') ||
    dokument.querySelector('.cky-modal.cky-modal-open') ||
    dokument.querySelector('.booking-modal.is-open'),
  );
}

/** Seite trägt den Ausschluss-Marker (z. B. die Messe-Seiten, doppelt zu `/lp`). */
export function seiteTraegtMarker(dokument: Pick<Document, 'querySelector'>) {
  return Boolean(dokument.querySelector(`[${BANNER_MARKER}]`));
}
